// Central copy source for the "Specific error messages for Provider Search 2.0" story.
// Keeping all wording here means content updates only require editing this one file,
// not every component that can trigger one of these error states.
//
// See reports/SPECIFIC_ERROR_MESSAGES_PLAN.md for the full implementation plan.

// Generic, last-resort Contact Us URL used only if a plan has no usable contact URL
// at all (see getErrorContactUsUrl() in PlanFeaturesContext.jsx).
export const DEFAULT_CONTACT_US_URL = 'https://www.aetna.com/about-us/contact-aetna.html';

// Plan-specific Contact Us URL overrides. Keyed by planFeatures.planName (the short
// plan code, e.g. "AZ", "VA-DSNP"). These take priority over the raw contactUsURL
// field from the plan-features API because several of these plans currently have
// missing ("NA") or incorrectly-scoped contactUsURL values (see
// reports/PLAN_FEATURE_LINK_AUDIT.md).
export const CONTACT_US_URL_OVERRIDES = {
  'AZ': 'https://www.mercycareaz.org/contact-us.html', // Mercy Care
  'IL-DSNP': 'https://www.aetna.com/about-us/contact-aetna.html', // IL FIDE
  'NJ-DSNP': 'https://www.aetnabetterhealth.com/new-jersey-hmosnp/contact-us.html', // NJ FIDE
  'VA-DSNP': 'https://www.aetnabetterhealth.com/virginia-hmosnp/contact-us', // VA FIDE
  'MI-DSNP': 'https://www.aetna.com/about-us/contact-aetna.html', // MI HIDE
};

// Categories of integration failures this story covers. Used as the `type` prop for
// <ApiErrorBanner /> and as keys into ERROR_MESSAGES below.
export const ERROR_TYPES = {
  API: 'api',
  LOCATION: 'location',
  TRANSLATION: 'translation',
  EMAIL: 'email',
};

// Each category has a list of approved message options (content owner can pick/swap
// which one is "active" without needing a code change beyond this file) plus the
// short/inline "copy" version used in compact banners, and fallback behavior notes
// for reference (fallback behavior itself is implemented in the relevant
// component/context, not derived from this file).
export const ERROR_MESSAGES = {
  [ERROR_TYPES.API]: {
    title: "Our provider search tool isn't working right now.",
    body: 'Try again in a few minutes.',
    options: [
      "We're temporarily unable to retrieve provider information. Please try again in a few minutes.",
      'Our search service is currently unavailable. Your request could not be completed at this time.',
      'Provider search is experiencing a temporary interruption. Please refresh the page or try again later.',
      "We're having trouble connecting to our services. Please try again shortly.",
    ],
    active: 0,
    shortCopy: "Our provider search tool isn't working right now. Try again in a few minutes.",
    fallback: 'None — search cannot proceed until the backend is reachable again.',
  },
  [ERROR_TYPES.LOCATION]: {
    title: "Locations and maps aren't working right now.",
    body: 'You can still search by ZIP code.',
    options: [
      'Location services are currently unavailable. You can continue your search by entering a ZIP code.',
      "We're unable to calculate distances at this time.",
      'Map and location features are temporarily unavailable.',
      'Search results are available, but map functionality may be limited.',
    ],
    active: 0,
    shortCopy: "Locations and maps aren't working right now. You can still search by ZIP code.",
    fallback: 'Continue showing provider results; hide map pins/distance; allow ZIP-code searching.',
  },
  [ERROR_TYPES.TRANSLATION]: {
    title: "Translation isn't working right now.",
    body: 'Try again in a few minutes.',
    options: [
      'Translation services are temporarily unavailable. The page is displayed in English.',
      'We were unable to load translated content at this time.',
      'Language assistance is currently unavailable. Please try again later.',
    ],
    active: 0,
    shortCopy: "Translation isn't working right now. Try again in a few minutes.",
    fallback: 'Default to English; do not block provider search.',
  },
  [ERROR_TYPES.EMAIL]: {
    title: "Email isn't working right now.",
    body: 'You may be able to download or print your results.',
    options: [
      'Your request was received, but we were unable to send the email confirmation at this time.',
      'Email delivery is temporarily unavailable. Please try again later.',
      'We could not send your search results via email. Please download or print the results instead.',
    ],
    active: 0,
    shortCopy: "Email isn't working right now. You may be able to download or print your results.",
    fallback: 'Save the request for retry if possible; allow user to download/print results instead of emailing.',
  },
};

// Single generic message for ANY email-format validation issue (missing "@", missing
// domain, missing ".com", etc.) — replaces the old per-format-error style message.
export const EMAIL_FORMAT_ERROR_MESSAGE = 'Be sure to fill in your email address like this: example@domain.com';

// Convenience getter for the currently "active" message for a given error type.
export const getErrorMessage = (type) => {
  const entry = ERROR_MESSAGES[type];
  if (!entry) return '';
  return entry.options[entry.active] ?? entry.options[0] ?? '';
};

// Convenience getter for the compact/inline copy used in smaller banners.
export const getShortErrorCopy = (type) => ERROR_MESSAGES[type]?.shortCopy ?? '';

// Convenience getters for the two-line card layout used by <ApiErrorBanner variant="full">
// (Figma node 11228-3543): a larger "Title/Section Title" line followed by a
// "Body/Medium" line (with the Contact Us link appended to it).
export const getErrorTitle = (type) => ERROR_MESSAGES[type]?.title ?? '';
export const getErrorBody = (type) => ERROR_MESSAGES[type]?.body ?? '';
